import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Grid,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { Formik, Form } from "formik";
import { useState } from "react";
import { passwordResetValidation } from "../../utility/validation/passwordResetValidation";
import { IPasswordResetPayload } from "../../utility/types/userType";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store/store";
import { passWordReset } from "../../redux/actions/userActions";
import { colors } from "../../utility/types/constant";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PasswordResetVerify from "./PasswordResetVerify";

const BENEFITS = [
  "Create or join savings circles with people you trust",
  "Automated contributions and scheduled payouts",
  "Bank-level security powered by Stripe",
  "Available in 140+ countries worldwide",
];

const ForgotPasswordForm = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [showVerifyAndResetPasswordModal, setShowVerifyAndResetPasswordModal] =
    useState(false);

  const [successAlert, setSuccessAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessageAlert, setErrorMessageAlert] = useState("");

  const initialValues = {
    email: "",
  };

  const handleSubmit = async (values: IPasswordResetPayload) => {
    setIsLoading(true);
    try {
      await dispatch(passWordReset({ email: values.email })).unwrap();
      setSuccessAlert(true);
      setTimeout(() => {
        setShowVerifyAndResetPasswordModal(true);
      }, 5000);
    } catch (err: any) {
      setSuccessAlert(false);
      const message =
        (typeof err === "string" && err) ||
        err?.message ||
        err?.error ||
        "We couldn't find an account with that email address. Please check and try again.";
      setErrorMessageAlert(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSuccessAlert = () => setSuccessAlert(false);
  const handleCloseErrorAlert = () => setErrorMessageAlert("");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.primary,
        display: "flex",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <CssBaseline />

      {/* Background decorative blobs */}
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.07)} 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.05)} 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      {/* ── Main layout ── */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: 1100,
          mx: "auto",
          alignItems: "center",
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, md: 0 },
          minHeight: "100vh",
        }}
      >
        {/* ── Left branding panel (desktop only) ── */}
        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            flex: "0 0 46%",
            pr: 8,
            py: 6,
          }}
        >
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
              }}
            >
              <Box
                component="img"
                src="/images/how-it-works-images/primary-logo-1-1.png"
                alt="FundsyFi"
                sx={{ width: 42, height: 42, objectFit: "contain", display: "block" }}
              />
            </Box>
            <Typography
              sx={{
                color: "#fff",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 800,
                fontSize: "1.5rem",
                letterSpacing: 0.3,
              }}
            >
              FundsyFi
            </Typography>
          </Box>

          <Typography
            sx={{
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 800,
              fontSize: { lg: "2.2rem", xl: "2.6rem" },
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            Reset your{" "}
            <Box component="span" sx={{ color: colors.mainSecondary }}>
              password.
            </Box>
          </Typography>

          <Typography
            sx={{
              color: alpha("#fff", 0.55),
              mb: 4,
              lineHeight: 1.7,
              fontSize: "0.95rem",
              maxWidth: 380,
            }}
          >
            Enter the email address linked to your account and we'll send you a
            verification code to reset your password.
          </Typography>

          {BENEFITS.map((benefit, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
              <CheckCircleIcon
                sx={{ color: colors.mainSecondary, fontSize: "1.1rem", mt: 0.3, flexShrink: 0 }}
              />
              <Typography sx={{ color: alpha("#fff", 0.75), fontSize: "0.88rem", lineHeight: 1.55 }}>
                {benefit}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── Right form card ── */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: { xs: 0, lg: 4 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 420,
              bgcolor: "#fff",
              borderRadius: { xs: 3, sm: 4 },
              p: { xs: 3, sm: 4 },
              boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* Mobile logo */}
            <Box sx={{ display: { xs: "flex", lg: "none" }, alignItems: "center", gap: 1, mb: 2.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src="/images/how-it-works-images/primary-logo-1-1.png"
                  alt="FundsyFi"
                  sx={{ width: 32, height: 32, objectFit: "contain", display: "block" }}
                />
              </Box>
              <Typography
                sx={{
                  color: colors.primary,
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 800,
                  fontSize: "1.1rem",
                }}
              >
                FundsyFi
              </Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 800,
                color: colors.primary,
                fontSize: "1.4rem",
                mb: 0.4,
              }}
            >
              Forgot your password?
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5, lineHeight: 1.5 }}>
              Enter your email and we'll send you a reset code.
            </Typography>

            {successAlert && (
              <Alert severity="success" onClose={handleCloseSuccessAlert} sx={{ mb: 2, py: 0.5 }}>
                Check your inbox — a verification code is on its way.
              </Alert>
            )}

            {errorMessageAlert && (
              <Alert severity="error" onClose={handleCloseErrorAlert} sx={{ mb: 2, py: 0.5 }}>
                {errorMessageAlert}
              </Alert>
            )}

            <Formik
              initialValues={initialValues}
              validationSchema={passwordResetValidation}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange }) => (
                <Form>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField
                      size="small"
                      error={!!(errors?.email && touched?.email)}
                      helperText={errors.email && touched.email && errors.email}
                      required
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={values.email}
                      onChange={handleChange}
                    />

                    <Button
                      disabled={isLoading}
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 0.5,
                        bgcolor: colors.mainSecondary,
                        color: colors.primary,
                        fontWeight: 800,
                        py: 1.2,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "0.95rem",
                        letterSpacing: 0.3,
                        boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
                        "&:hover": {
                          bgcolor: colors.mainSecondary,
                          boxShadow: `0 6px 20px ${alpha(colors.mainSecondary, 0.5)}`,
                        },
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={22} sx={{ color: colors.primary }} />
                      ) : (
                        "Send Reset Code"
                      )}
                    </Button>

                    <Grid container justifyContent="space-between">
                      <Grid item>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
                          Remembered it?{" "}
                          <Box
                            component="a"
                            href="/circle/sign-in"
                            sx={{
                              color: colors.mainSecondary,
                              fontWeight: 700,
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            Sign In
                          </Box>
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
                          No account?{" "}
                          <Box
                            component="a"
                            href="/circle/sign-up"
                            sx={{
                              color: colors.mainSecondary,
                              fontWeight: 700,
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            Sign Up
                          </Box>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Form>
              )}
            </Formik>

            <Typography
              variant="caption"
              sx={{ display: "block", textAlign: "center", mt: 2.5, color: "text.disabled" }}
            >
              © {new Date().getFullYear()} FundsyFi
            </Typography>
          </Box>
        </Box>
      </Box>

      {showVerifyAndResetPasswordModal && (
        <PasswordResetVerify
          open={showVerifyAndResetPasswordModal}
          handleClose={() => setShowVerifyAndResetPasswordModal(false)}
        />
      )}
    </Box>
  );
};

export default ForgotPasswordForm;
